import React from "react";
import { cn } from "../../lib/utils";
import { Badge } from "../ui/badge";
import { NewBadge } from "../../../../../features/billValidation/components/NewBadge";
import {
  Shield,
  Gauge,
  Network,
  Download,
  Clock,
  CalendarDays,
  Settings2,
  Image,
  Tags,
  SlidersHorizontal,
} from "lucide-react";

export type AdminTab = "loss-factors" | "network-tariffs" | "nta-thresholds" | "tou-rates" | "charge-map" | "holidays" | "export" | "config" | "state-images";

interface AdminSidebarProps {
  activeTab: AdminTab;
  onTabChange: (tab: AdminTab) => void;
}

export const AdminSidebar: React.FC<AdminSidebarProps> = ({
  activeTab,
  onTabChange,
}) => {
  const tabs: { id: AdminTab; label: string; icon: React.ElementType; description: string; badge?: number; isNew?: boolean }[] = [
    {
      id: "loss-factors",
      label: "Loss Factors",
      icon: Gauge,
      description: "Manage MLF & DLF reference data",
    },
    {
      id: "network-tariffs",
      label: "Network Tariffs",
      icon: Network,
      description: "Manage distribution tariff codes",
    },
    {
      id: "nta-thresholds",
      label: "NTA Thresholds",
      icon: SlidersHorizontal,
      description: "Tariff eligibility for Network Tariff Analysis",
      isNew: true,
    },
    {
      id: "tou-rates",
      label: "Retailer TOU Rates",
      icon: Clock,
      description: "Manage retailer time-of-use rate windows",
    },
    {
      id: "charge-map",
      label: "Retailer Charge Map",
      icon: Tags,
      description: "Map bill wording to expected charge items",
    },
    {
      id: "holidays",
      label: "Public Holidays",
      icon: CalendarDays,
      description: "Manage Australian public holiday data",
    },
    {
      id: "config",
      label: "System Config",
      icon: Settings2,
      description: "Global defaults & parameters",
    },
    {
      id: "state-images",
      label: "State Images",
      icon: Image,
      description: "Market chart shown per state in proposals",
    },
    {
      id: "export",
      label: "Export",
      icon: Download,
      description: "Export clients, sites, contracts & rates",
    },
  ];

  return (
    <div className="w-64 bg-card border-r border-border h-full">
      <div className="p-6">
        <div className="flex items-center gap-2 mb-6">
          <Shield className="h-6 w-6 text-primary" />
          <h2 className="text-lg font-semibold">Admin</h2>
        </div>

        <nav className="space-y-2">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;

            return (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors",
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "hover:bg-muted text-muted-foreground hover:text-foreground"
                )}
              >
                <Icon className="h-5 w-5" />
                <div className="flex-1">
                  <div className="flex items-center gap-1.5 font-medium">{tab.label}{tab.isNew && <NewBadge />}</div>
                  <div
                    className={cn(
                      "text-xs",
                      isActive
                        ? "text-primary-foreground/70"
                        : "text-muted-foreground"
                    )}
                  >
                    {tab.description}
                  </div>
                </div>
                {tab.badge && (
                  <Badge
                    variant={isActive ? "secondary" : "default"}
                    className="text-xs"
                  >
                    {tab.badge}
                  </Badge>
                )}
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );
};
